import React from 'react';

import { type AnalyticsProps } from '../../helpers/analytics';
import { useImpressionRef } from '../../helpers/use-impression-ref';
import { cn } from '../../utils/cn';
import { Container, type ContainerProps } from '../../utils/container';

/**
 * Box component props. Accepts all standard `<div>` attributes plus an
 * optional `as` prop to render as a semantic element (e.g. `aside`, `nav`).
 * Passing `onClick` renders the Box as a `<button>` (requires `aria-label`);
 * passing `href` renders the Box as an `<a>` (requires `aria-label`).
 *
 * @example
 * ```tsx
 * <Box>Content</Box>
 * <Box as="aside" className="gap-4 p-6">…</Box>
 * <Box onClick={open} aria-label="Open settings">…</Box>
 * <Box href="/foo" aria-label="Go to foo">…</Box>
 * ```
 */
export type BoxProps = ContainerProps & AnalyticsProps;

/**
 * Flex container. Applies `flex` by default; pass `className` to extend
 * or override (tailwind-merge resolves conflicts so user classes win).
 *
 * @example
 * ```tsx
 * <Box className="gap-2 items-center">…</Box>
 * <Box as="nav" className="flex-col gap-4">…</Box>
 * ```
 */
export const Box = React.forwardRef<HTMLElement, BoxProps>(
  ({ className, children, analyticsName, trackImpression, ...props }, ref) => {
    const viewRef = useImpressionRef(
      Boolean(analyticsName && trackImpression),
      analyticsName,
      'boxViewEvent',
      ref,
    );

    return (
      <Container ref={viewRef} className={cn('flex', className)} {...props}>
        {children}
      </Container>
    );
  },
);

Box.displayName = 'Box';

/** Row props are identical to `BoxProps`. */
export type RowProps = BoxProps;

/**
 * Convenience wrapper that renders a `<Box>` with `flex-row` applied.
 * Accepts all the same props as Box; `className` can still override.
 *
 * @example
 * ```tsx
 * <Row className="gap-2 items-center">…</Row>
 * ```
 */
export const Row = React.forwardRef<HTMLElement, RowProps>(({ className, ...props }, ref) => {
  return <Box ref={ref} className={cn('flex-row', className)} {...props} />;
});

Row.displayName = 'Row';

/** Col props are identical to `BoxProps`. */
export type ColProps = BoxProps;

/**
 * Convenience wrapper that renders a `<Box>` with `flex-col` applied.
 * Accepts all the same props as Box; `className` can still override.
 *
 * @example
 * ```tsx
 * <Col className="gap-2">…</Col>
 * ```
 */
export const Col = React.forwardRef<HTMLElement, ColProps>(({ className, ...props }, ref) => {
  return <Box ref={ref} className={cn('flex-col', className)} {...props} />;
});

Col.displayName = 'Col';
